import { type ApiClient } from "@decizyon/api-client";

import type {
  BoardTicket,
  ProductAgentBindingPage,
  ProductCreateResult,
  ProductIntegrationSetupReadResult,
  ProductIntegrationSetupResult,
  ProductPage,
  SupportSessionPage,
  TicketBoardDataSource,
  TicketDetailResponse,
  TicketPage,
  TicketReplyResult,
  TicketStageResult,
} from "./ticketBoardTypes";
import { subscribeToTicketBoard } from "./ticketBoardRealtime";

function withQuery(path: string, query: Readonly<Record<string, string | number | undefined>>): string {
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    if (value !== undefined) search.set(name, String(value));
  }
  const serialized = search.toString();
  return serialized.length === 0 ? path : `${path}?${serialized}`;
}

export function createApiTicketBoardDataSource(
  apiClient: ApiClient,
  realtimeUrl?: string | URL,
): TicketBoardDataSource {
  return {
    getTicketDetail: async (query, signal) => await apiClient.request<TicketDetailResponse>(
      withQuery(`tickets/${encodeURIComponent(query.ticketId)}`, {
        limit: query.limit,
        messageCursor: query.messageCursor,
      }),
      {
        additionalProtectedScopes: [`product:${query.productId}`],
        protectedScope: "capability:tickets:read",
        ...(signal === undefined ? {} : { signal }),
      },
    ),
    listProductAgentBindings: async (query, signal) => await apiClient.request<ProductAgentBindingPage>(
      withQuery("product-agent-bindings", query),
      {
        protectedScope: "capability:catalog:read",
        ...(signal === undefined ? {} : { signal }),
      },
    ),
    listProducts: async (signal) => await apiClient.request<ProductPage>("products?limit=100", {
      protectedScope: "capability:catalog:read",
      ...(signal === undefined ? {} : { signal }),
    }),
    createProduct: async (command) => await apiClient.mutate<ProductCreateResult, {
      readonly name: string;
      readonly status?: "active" | "inactive";
    }>("products", {
      body: {
        name: command.name,
        ...(command.status === undefined ? {} : { status: command.status }),
      },
      idempotencyKey: command.idempotencyKey,
      method: "POST",
      protectedScope: "capability:catalog:write",
    }),
    readProductIntegrationSetup: async (productId, signal) => (
      await apiClient.request<ProductIntegrationSetupReadResult>(
        `products/${encodeURIComponent(productId)}/integration-setup`,
        {
          protectedScope: "capability:catalog:read",
          ...(signal === undefined ? {} : { signal }),
        },
      )
    ),
    setupProductIntegration: async (command) => (
      await apiClient.mutate<ProductIntegrationSetupResult, {
        readonly accountId: string;
        readonly allowedOrigins: readonly string[];
        readonly credentialVersion: number;
        readonly integrationId: string;
        readonly secretReference: string;
      }>(`products/${encodeURIComponent(command.productId)}/integration-setup`, {
        body: {
          accountId: command.accountId,
          allowedOrigins: command.allowedOrigins,
          credentialVersion: command.credentialVersion,
          integrationId: command.integrationId,
          secretReference: command.secretReference,
        },
        idempotencyKey: command.idempotencyKey,
        method: "POST",
        protectedScope: "capability:catalog:write",
      })
    ),
    listTickets: async (query, signal) => await apiClient.request<TicketPage>(withQuery("tickets", query), {
      protectedScope: "capability:tickets:read",
      ...(signal === undefined ? {} : { signal }),
    }),
    listSessions: async (query, signal) => await apiClient.request<SupportSessionPage>(
      withQuery("support/sessions", query),
      {
        protectedScope: "capability:sessions:read",
        ...(signal === undefined ? {} : { signal }),
      },
    ),
    getTicket: async (ticketId, signal) => {
      const response = await apiClient.request<{ readonly ticket: BoardTicket }>(
        `tickets/${encodeURIComponent(ticketId)}?limit=1`,
        {
          protectedScope: "capability:tickets:read",
          ...(signal === undefined ? {} : { signal }),
        },
      );
      return response.ticket;
    },
    moveTicket: async (command) => await apiClient.mutate<TicketStageResult, {
      readonly expectedVersion: number;
      readonly stage: typeof command.stage;
    }>(`tickets/${encodeURIComponent(command.ticketId)}/stage`, {
      additionalProtectedScopes: [`product:${command.productId}`],
      body: { stage: command.stage, expectedVersion: command.expectedVersion },
      idempotencyKey: command.idempotencyKey,
      method: "PATCH",
      protectedScope: "capability:tickets:transition",
    }),
    replyToTicket: async (command) => await apiClient.mutate<TicketReplyResult, {
      readonly message: string;
    }>(
      `tickets/${encodeURIComponent(command.ticketId)}/replies`,
      {
        additionalProtectedScopes: [`product:${command.productId}`],
        body: { message: command.message },
        idempotencyKey: command.idempotencyKey,
        method: "POST",
        protectedScope: "capability:tickets:reply",
      },
    ),
    subscribeToTicketBoard: (resourceId) => (
      subscribeToTicketBoard(apiClient, resourceId, realtimeUrl)
    ),
  };
}
